/* Scroll engine: Lenis smooth scroll + GSAP ScrollTrigger.
   Signature effect: a dirty window that the robot wipes clean as you scroll. */
(function () {
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches || /[?&]static\b/.test(location.search);
  const hasGsap = typeof gsap !== 'undefined' && typeof ScrollTrigger !== 'undefined';
  document.documentElement.classList.add(hasGsap && !reduce ? 'fx' : 'nofx');

  if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
  if (hasGsap) ScrollTrigger.config({ ignoreMobileResize: true });

  /* ---------- smooth scroll ---------- */
  let lenis = null;
  if (hasGsap && !reduce && typeof Lenis !== 'undefined') {
    lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    lenis.on('scroll', ScrollTrigger.update);
    gsap.ticker.add((t) => lenis.raf(t * 1000));
    gsap.ticker.lagSmoothing(0);
  }
  document.querySelectorAll('a[href^="#"]').forEach((a) => {
    a.addEventListener('click', (e) => {
      const id = a.getAttribute('href');
      if (id.length < 2) return;
      const el = document.querySelector(id);
      if (!el) return;
      e.preventDefault();
      if (lenis) lenis.scrollTo(el, { offset: -70 });
      else el.scrollIntoView({ behavior: 'smooth' });
    });
  });

  /* ---------- nav state ---------- */
  const nav = document.querySelector('.nav');
  const buybar = document.querySelector('.buybar');
  const onScroll = () => {
    const y = window.scrollY;
    if (nav) nav.classList.toggle('solid', y > 40);
    if (buybar) {
      const offer = document.getElementById('offer');
      const st = document.querySelector('.tstrip');
      const past = st ? st.getBoundingClientRect().top < window.innerHeight * 0.3 : y > window.innerHeight;
      // hide the pill while the buy box, the offer banner or the footer is on screen
      const onScreen = (el) => { if (!el) return false; const r = el.getBoundingClientRect(); return r.top < window.innerHeight && r.bottom > 0; };
      const blocked = onScreen(offer) || onScreen(document.querySelector('.final')) || onScreen(document.querySelector('footer'));
      const show = past && !blocked;
      buybar.classList.toggle('show', show);
      document.documentElement.classList.toggle('bb-on', show);
      if (show) document.documentElement.classList.add('bb-seen');
      document.documentElement.style.setProperty('--pillh', buybar.offsetHeight + 'px');
    }
  };
  window.addEventListener('scroll', onScroll, { passive: true });
  onScroll();
  if ('IntersectionObserver' in window) {
    const io = new IntersectionObserver(() => onScroll(), { threshold: [0, 0.01] });
    ['#offer', '.final', 'footer'].forEach((s) => { const el = document.querySelector(s); if (el) io.observe(el); });
  }

  /* ---------- hero: wipe the window clean ---------- */
  const hero = document.querySelector('.wipe');
  if (hero && hasGsap && !reduce) {
    const canvas = hero.querySelector('canvas');
    const robot = hero.querySelector('.wipe-robot');
    const ctx = canvas.getContext('2d');
    let W = 0, H = 0, dpr = 1, path = [], dirt = null;
    const RA = 0.584; // robot cutout width / height

    // real photo of the same window, very dirty; drawn with the same object-fit:cover math as the clean <img>
    const dirtImg = new Image();
    const pickSrc = () => (window.innerWidth <= 700 ? 'img/window-dirty-tall-900.webp?v=2' : 'img/window-dirty-wide-1800.webp?v=2');
    function makeDirt() {
      const c = document.createElement('canvas');
      c.width = W; c.height = H;
      const g = c.getContext('2d');
      if (dirtImg.complete && dirtImg.naturalWidth) {
        const s = Math.max(W / dirtImg.naturalWidth, H / dirtImg.naturalHeight);
        const dw = dirtImg.naturalWidth * s, dh = dirtImg.naturalHeight * s;
        g.drawImage(dirtImg, (W - dw) / 2, (H - dh) / 2, dw, dh);
      } else {
        g.fillStyle = 'rgba(200,192,176,.5)'; g.fillRect(0, 0, W, H);
      }
      return c;
    }

    function makePath() {
      // zig-zag S-path like a real window robot, top to bottom, covering the whole pane
      // start fully inside the frame: x >= 24 px, y >= header (68 px) + 24 px
      const b = brush(), rw = b * 0.6, rh = rw / RA, top = 92 * dpr + rh / 2, bot = H - rh * 0.5 - 24 * dpr, mx = rw * 0.5 + 24 * dpr;
      const rows = Math.max(3, Math.ceil((bot - top) / (rh * 0.9)) + 1);
      const pts = [];
      for (let r = 0; r < rows; r++) {
        const y = top + (bot - top) * (r / (rows - 1));
        const a = r % 2 ? W - mx : mx, c = r % 2 ? mx : W - mx;
        pts.push([a, y], [c, y]);
      }
      const out = [];
      for (let i = 0; i < pts.length - 1; i++) {
        const [x1, y1] = pts[i], [x2, y2] = pts[i + 1];
        const n = Math.max(2, Math.hypot(x2 - x1, y2 - y1) / (4 * dpr));
        for (let k = 0; k < n; k++) out.push([x1 + (x2 - x1) * (k / n), y1 + (y2 - y1) * (k / n)]);
      }
      out.push(pts[pts.length - 1]);
      return out;
    }

    document.documentElement.classList.add('hero-live');
    let drawnTo = 0;
    function brush() { return W < 700 * dpr ? W * 0.36 : Math.min(W * 0.14, H * 0.24); }
    function redraw(p) {
      const idx = Math.floor(p * (path.length - 1));
      if (idx < drawnTo) {                               // scrolling back: repaint dirt
        ctx.globalCompositeOperation = 'source-over';
        ctx.clearRect(0, 0, W, H);
        ctx.drawImage(dirt, 0, 0);
        drawnTo = 0;
      }
      ctx.globalCompositeOperation = 'destination-out';
      const b = brush();
      for (let i = drawnTo; i <= idx; i++) {
        const [x, y] = path[i];
        ctx.fillStyle = 'rgba(0,0,0,1)';
        ctx.beginPath();
        const rw = b * 0.6, rh = rw / RA;
        ctx.roundRect ? ctx.roundRect(x - rw / 2, y - rh / 2, rw, rh, rw * 0.45) : ctx.rect(x - rw / 2, y - rh / 2, rw, rh);
        ctx.fill();
      }
      drawnTo = idx;
      if (p >= 0.999) { ctx.clearRect(0, 0, W, H); }
      const [x, y] = path[idx] || path[0];
      if (robot) {
        const w = b * 0.6 / dpr, h = w / RA;
        robot.style.width = w + 'px';
        robot.style.transform = `translate(${x / dpr - w / 2}px, ${y / dpr - h / 2}px)`;
      }
    }

    function size() {
      dpr = Math.min(window.devicePixelRatio || 1, 2);
      const r = canvas.getBoundingClientRect();
      W = Math.round(r.width * dpr); H = Math.round(r.height * dpr);
      if (!W || !H) return;
      canvas.width = W; canvas.height = H;
      dirt = makeDirt(); path = makePath();
      ctx.globalCompositeOperation = 'source-over';
      ctx.drawImage(dirt, 0, 0); drawnTo = 0;
    }
    size();
    let prog = 0;
    dirtImg.onload = () => { size(); redraw(Math.min(1, prog / 0.88)); };
    dirtImg.src = pickSrc();

    if (hasGsap && !reduce) {
      const st = ScrollTrigger.create({
        trigger: hero, start: 'top top', end: () => '+=' + Math.round(hero.offsetHeight * (window.innerWidth <= 700 ? 1 : 0.5)), refreshPriority: 2,
        pin: true, scrub: 0.4,
        onUpdate: (self) => {
          prog = self.progress;
          redraw(Math.min(1, prog / 0.88));
          hero.style.setProperty('--p', prog.toFixed(3));
          hero.classList.toggle('done', prog > 0.9);
          document.documentElement.classList.toggle('hero-live', prog < 0.999);
        },
      });
      redraw(0);
      let rt, lastW = window.innerWidth;
      window.addEventListener('resize', () => {
        if (window.innerWidth === lastW) return;          // ignore mobile URL-bar height changes
        lastW = window.innerWidth;
        clearTimeout(rt);
        rt = setTimeout(() => { if (!dirtImg.src.endsWith(pickSrc())) { dirtImg.src = pickSrc(); return; } size(); redraw(Math.min(1, prog / 0.88)); ScrollTrigger.refresh(); }, 200);
      });
      void st;
    }
  }

  if (!hasGsap || reduce) {
    initStatic();
    return;
  }

  /* ---------- reveals ---------- */
  gsap.utils.toArray('[data-rev]').forEach((el) => {
    gsap.from(el, {            // transform only: content stays visible at rest (no empty bands)
      y: 24, duration: 0.8, ease: 'power3.out',
      delay: parseFloat(el.dataset.rev) || 0,
      scrollTrigger: { trigger: el, start: 'top 88%' },
    });
  });

  // parallax images
  gsap.utils.toArray('[data-par]').forEach((el) => {
    const amt = parseFloat(el.dataset.par) || 12;
    gsap.fromTo(el, { yPercent: -amt }, {
      yPercent: amt, ease: 'none',
      scrollTrigger: { trigger: el.parentElement, start: 'top bottom', end: 'bottom top', scrub: true },
    });
  });

  initStatic();
  ScrollTrigger.sort();
  window.addEventListener('load', () => {
    ScrollTrigger.sort();
    ScrollTrigger.refresh();
    // deep links (e.g. vergleich.html -> #angebot): jump after pins have added their spacing
    if (location.hash.length > 1) {
      const t = document.querySelector(location.hash);
      if (!t) return;
      // re-aim for ~3 s: lazy images and pin spacers keep shifting the target after load
      let tries = 0, stop = false;
      const halt = () => { stop = true; };
      ['wheel', 'touchstart', 'keydown'].forEach((e) => window.addEventListener(e, halt, { once: true, passive: true }));
      const aim = () => {
        if (stop) return;
        const d = t.getBoundingClientRect().top - 70;
        if (Math.abs(d) > 3) {
          const y = window.scrollY + d;
          if (lenis) lenis.scrollTo(y, { immediate: true, force: true }); else window.scrollTo(0, y);
          ScrollTrigger.update();
        }
        if (++tries < 30) setTimeout(aim, 250);
      };
      aim();
    }
  });

  /* ---------- checkout: Shopify cart permalink per pack, ad + ClickMagick params kept for the visit ---------- */
  function rvTrack() {
    const k = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'gclid', 'gbraid', 'wbraid', 'fbclid', 'cmc_vid', 'cmc_adid'];
    const q = new URLSearchParams(location.search); let t = {};
    try { t = JSON.parse(sessionStorage.getItem('rv_track') || '{}'); } catch (e) {}
    k.forEach((n) => { const v = q.get(n); if (v) t[n] = v.slice(0, 250); });
    try { sessionStorage.setItem('rv_track', JSON.stringify(t)); } catch (e) {}
    return t;
  }
  /* keys are prefixed 'ad_': cmc.js rewrites any [utm_source]/[gclid]/[cmc_vid] text inside links into its value */
  function rvCart(n) {
    const v = (window.RV_VARIANTS || [])[(parseInt(n, 10) || 1) - 1];
    if (!v) return '#offer';
    const u = new URLSearchParams(), cm = window.clickmagick_cmc; u.set('attributes[ad_landing_page]', 'revio-us');
    const t = rvTrack(); Object.keys(t).forEach((k) => u.set('attributes[ad_' + k + ']', t[k]));
    if (cm && cm.vid) u.set('attributes[ad_cm_visitor]', cm.vid);
    return '/cart/' + v + ':1?' + u.toString();
  }

  /* ---------- interactions that work without GSAP ---------- */
  function initStatic() {
    // before/after slider
    document.querySelectorAll('.ba').forEach((ba) => {
      const r = ba.querySelector('input');
      const set = () => ba.style.setProperty('--x', r.value + '%');
      r.addEventListener('input', set); set();
    });
    // bundle picker: 1 / 2 / 3 cameras, price per tier
    const bundles = document.querySelectorAll('.bundle input');
    const total = document.querySelector('[data-total]');
    const bb = document.querySelector('[data-bb-price]');
    const cta = document.querySelector('[data-buy]');
    const fmt = (v) => '$' + v.toFixed(2);
    const pick = () => {
      const on = document.querySelector('.bundle input:checked'); if (!on) return;
      document.querySelectorAll('.bundle').forEach((l) => l.classList.toggle('sel', l.contains(on)));
      const t = parseFloat(on.dataset.total);
      if (total) total.textContent = fmt(t);
      if (bb) bb.textContent = fmt(t);
      if (cta) { cta.dataset.n = on.value; cta.href = rvCart(on.value); cta.textContent = on.value === '1' ? 'Add to cart' : 'Add ' + on.value + ' cameras to cart'; }
    };
    bundles.forEach((r) => r.addEventListener('change', pick)); pick();
    document.querySelectorAll('[data-buy]').forEach((b) => b.addEventListener('click', () => { const on = document.querySelector('.bundle input:checked'); b.href = rvCart(on ? on.value : '1'); }));
    // gallery thumbs
    const main = document.querySelector('[data-main]');
    const thumbs = document.querySelectorAll('.thumbs button');
    thumbs.forEach((t) => t.addEventListener('click', () => {
      if (!main) return;
      main.src = t.dataset.src; main.alt = t.dataset.alt || '';
      thumbs.forEach((x) => x.classList.toggle('on', x === t));
    }));
    // faq + accordions
    document.querySelectorAll('.faq details, .acc details').forEach((d) => {
      d.addEventListener('toggle', () => { if (typeof ScrollTrigger !== 'undefined') ScrollTrigger.refresh(); });
    });
    // checkout placeholder
    document.querySelectorAll('[data-buy]').forEach((b) => b.addEventListener('click', (e) => {
      if (b.getAttribute('href') === '#checkout') {
        e.preventDefault();
        const t = document.querySelector('.toast');
        if (t) { t.classList.add('show'); setTimeout(() => t.classList.remove('show'), 2600); }
      }
    }));
  }
})();

/* hero loop: muted problem -> solution dramatization; the poster stays if it can't play; paused off-screen;
   ?static=1 or reduced motion = end-state still. Three text lines fade in/out against the clip time. */
(function () {
  const vid = document.querySelector('.loop-vid');
  const box = document.querySelector('.vcap');
  const ps = box ? Array.prototype.slice.call(box.querySelectorAll('p')) : [];
  const show = (i) => ps.forEach((p, k) => p.classList.toggle('on', k === i));
  if (!vid) return;
  const mob = window.matchMedia('(max-width:600px)').matches;
  if (/[?&]static=1/.test(location.search)) {
    const pic = vid.parentNode.querySelector('picture');
    pic.querySelectorAll('source').forEach((x) => x.remove());
    pic.querySelector('img').src = vid.dataset[mob ? 'ms' : 'ds'];
    vid.remove(); show(ps.length - 1); return;
  }
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) { vid.removeAttribute('autoplay'); show(ps.length - 1); return; }
  let inView = true;
  const play = () => { const p = vid.play(); if (p && p.catch) p.catch(() => {}); };
  /* scene cues (s): problem 0-2.4 | place + robot cleans 2.4-7.3 | dip 7.3-7.8 | payoff 7.8-9.5 | seam dip 9.5-10.2 (loop 10.2 s) */
  const cue = (t) => (t >= 0.1 && t < 1.6) ? 0 : (t >= 1.9 && t < 6.0) ? 1 : (t >= 6.9 && t < 8.5) ? 2 : -1;
  let last = -2;
  const tick = () => { if (vid.readyState > 1) { const i = cue(vid.currentTime); if (i !== last) { show(i); last = i; } } requestAnimationFrame(tick); };
  vid.addEventListener('playing', () => { vid.classList.add('on'); requestAnimationFrame(tick); }, { once: true });
  if (mob && vid.dataset.mp) vid.poster = vid.dataset.mp;
  const start = () => { vid.muted = true; vid.src = vid.dataset[mob ? 'm' : 'd']; if (inView) play(); else vid.pause(); };
  if ('IntersectionObserver' in window) {
    new IntersectionObserver((es) => {
      inView = es[0].isIntersecting;
      if (!vid.getAttribute('src')) return;
      if (inView) play(); else vid.pause();
    }).observe(vid.parentNode);
  }
  if (document.readyState === 'complete') start(); else window.addEventListener('load', start);
})();
